import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { format, parseISO } from "date-fns";
import { Plus, PlusCircle, Loader2, Edit, Trash2, Calendar, MapPin, Ticket, Star, X, Info } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { ImageUpload } from "../../components/ImageUpload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminEvents = () => {
  const [activeTab, setActiveTab] = useState("published");
  const [events, setEvents] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [newTypeName, setNewTypeName] = useState("");
  const [creatingType, setCreatingType] = useState(false);
  
  const EDK_STUDIO_NAME = "Empire Dance Kenya Studio";
  const EDK_STUDIO_ADDRESS = "Kenrail Towers, 3rd Floor, Southern Wing, Westlands, Nairobi";

  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
    event_type: "workshop",
    venue_type: "",
    venue_name: "",
    venue_address: "",
    at_studio: false,
    start_date: "",
    start_time: "18:00",
    end_date: "",
    end_time: "21:00",
    cover_image: "",
    max_capacity: 100,
    status: "draft",
    is_featured: false,
    highlights: [],
  });

  const [newHighlight, setNewHighlight] = useState("");
  const [pendingTicketTypes, setPendingTicketTypes] = useState([]);
  const [deletedTicketTypeIds, setDeletedTicketTypeIds] = useState([]);
  const [newTicketForm, setNewTicketForm] = useState({
    name: "",
    ticket_type: "regular",
    price: 1500,
    quantity_available: 50,
    max_per_order: 10,
  });

  const TICKET_TYPE_COLORS = {
    early_bird: "bg-amber-500/20 text-amber-400 border-amber-500/30",
    regular: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    vip: "bg-purple-500/20 text-purple-400 border-purple-500/30",
  };
  const TICKET_TYPE_LABELS = { early_bird: "Early Bird", regular: "Regular", vip: "VIP" };

  useEffect(() => {
    fetchEvents();
    fetchEventTypes();
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/admin/events/all`);
      setEvents(res.data.events || res.data || []);
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEventTypes = async () => {
    try {
      const res = await axios.get(`${API}/admin/event-types`);
      setEventTypes(res.data || []);
    } catch (error) {
      console.error("Error fetching event types:", error);
    }
  };

  const handleEventTypeChange = (value) => {
    if (value === "__new__") {
      setTypeDialogOpen(true);
    } else {
      setFormData({ ...formData, event_type: value });
    }
  };

  const handleCreateEventType = async (e) => {
    e.preventDefault();
    if (!newTypeName.trim()) {
      toast.error("Type name is required");
      return;
    }
    setCreatingType(true);
    try {
      const res = await axios.post(`${API}/admin/event-types`, { name: newTypeName.trim() });
      const created = res.data;
      setEventTypes(prev => [...prev, created]);
      setFormData(prev => ({ ...prev, event_type: created.slug }));
      toast.success(`"${created.name}" event type created!`);
      setTypeDialogOpen(false);
      setNewTypeName("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create event type");
    } finally {
      setCreatingType(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      slug: "",
      description: "",
      event_type: "workshop",
      venue_type: "",
      venue_name: "",
      venue_address: "",
      at_studio: false,
      start_date: "",
      start_time: "18:00",
      end_date: "",
      end_time: "21:00",
      cover_image: "",
      max_capacity: 100,
      status: "draft",
      is_featured: false,
      highlights: [],
    });
    setNewHighlight("");
    setEditingEvent(null);
    setPendingTicketTypes([]);
    setDeletedTicketTypeIds([]);
    setNewTicketForm({ name: "", ticket_type: "regular", price: 1500, quantity_available: 50, max_per_order: 10 });
  };

  const addPendingTicketType = () => {
    if (!newTicketForm.name.trim() || !newTicketForm.price) return;
    setPendingTicketTypes(prev => [...prev, {
      ...newTicketForm,
      price: Number(newTicketForm.price),
      quantity_available: Number(newTicketForm.quantity_available),
      max_per_order: Number(newTicketForm.max_per_order),
    }]);
    setNewTicketForm({ name: "", ticket_type: "regular", price: 1500, quantity_available: 50, max_per_order: 10 });
  };

  const removeExistingTicketType = (id) => setDeletedTicketTypeIds(prev => [...prev, id]);
  const removePendingTicketType = (idx) => setPendingTicketTypes(prev => prev.filter((_, i) => i !== idx));

  const handleVenueTypeChange = (value) => {
    if (value === "edk_studio") {
      setFormData(prev => ({
        ...prev,
        venue_type: "edk_studio",
        venue_name: EDK_STUDIO_NAME,
        venue_address: EDK_STUDIO_ADDRESS,
        at_studio: true,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        venue_type: "other",
        venue_name: "",
        venue_address: "",
        at_studio: false,
      }));
    }
  };

  const handleOpenDialog = (event = null) => {
    setPendingTicketTypes([]);
    setDeletedTicketTypeIds([]);
    setNewTicketForm({ name: "", ticket_type: "regular", price: 1500, quantity_available: 50, max_per_order: 10 });
    if (event) {
      setEditingEvent(event);
      const startDate = event.start_datetime ? parseISO(event.start_datetime) : new Date();
      const endDate = event.end_datetime ? parseISO(event.end_datetime) : new Date();
      
      setFormData({
        name: event.name || "",
        slug: event.slug || "",
        description: event.description || "",
        event_type: event.event_type || "workshop",
        venue_type: event.at_studio ? "edk_studio" : (event.venue_name ? "other" : ""),
        venue_name: event.venue_name || "",
        venue_address: event.venue_address || "",
        at_studio: event.at_studio || false,
        start_date: format(startDate, "yyyy-MM-dd"),
        start_time: format(startDate, "HH:mm"),
        end_date: format(endDate, "yyyy-MM-dd"),
        end_time: format(endDate, "HH:mm"),
        cover_image: event.cover_image || "",
        max_capacity: event.max_capacity || 100,
        status: event.status || "draft",
        is_featured: event.is_featured || false,
        highlights: event.highlights || [],
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const isEditing = !!editingEvent;

    try {
      // Auto-stage any filled-but-not-yet-added ticket type
      const finalPendingTickets = [...pendingTicketTypes];
      if (newTicketForm.name.trim() && newTicketForm.price) {
        finalPendingTickets.push({
          ...newTicketForm,
          price: Number(newTicketForm.price),
          quantity_available: Number(newTicketForm.quantity_available),
          max_per_order: Number(newTicketForm.max_per_order),
        });
      }

      const slug = formData.slug || formData.name.toLowerCase().replace(/\s+/g, "-");
      const startDateTime = new Date(`${formData.start_date}T${formData.start_time}:00`);
      const endDateTime = new Date(`${formData.end_date || formData.start_date}T${formData.end_time}:00`);

      const payload = {
        name: formData.name,
        slug,
        description: formData.description,
        event_type: formData.event_type,
        venue_name: formData.venue_name,
        venue_address: formData.venue_address,
        at_studio: formData.at_studio,
        start_datetime: startDateTime.toISOString(),
        end_datetime: endDateTime.toISOString(),
        cover_image: formData.cover_image,
        max_capacity: Number(formData.max_capacity),
        status: formData.status,
        is_featured: formData.is_featured,
        highlights: formData.highlights,
      };

      let eventId;
      if (editingEvent) {
        await axios.put(`${API}/admin/events/${editingEvent.id}`, payload);
        eventId = editingEvent.id;
        await Promise.all(deletedTicketTypeIds.map(id =>
          axios.delete(`${API}/admin/events/${eventId}/ticket-types/${id}`)
        ));
      } else {
        const res = await axios.post(`${API}/admin/events`, payload);
        eventId = res.data.id;
      }

      await Promise.all(finalPendingTickets.map(tt =>
        axios.post(`${API}/admin/events/${eventId}/ticket-types`, tt)
      ));

      toast.success(isEditing ? "Event updated!" : "Event created!");
      setDialogOpen(false);
      resetForm();
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save event");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (eventId, eventName) => {
    toast.warning(`Delete "${eventName}"?`, {
      description: "This will permanently remove the event and all its ticket types. This cannot be undone.",
      action: {
        label: "Delete",
        onClick: async () => {
          try {
            await axios.delete(`${API}/admin/events/${eventId}`);
            toast.success("Event deleted");
            fetchEvents();
          } catch {
            toast.error("Failed to delete event");
          }
        },
      },
      cancel: { label: "Cancel", onClick: () => {} },
    });
  };

  const statusColors = {
    draft: "bg-neutral-500/20 text-neutral-400",
    published: "bg-green-500/20 text-green-400",
    cancelled: "bg-red-500/20 text-red-400",
    completed: "bg-blue-500/20 text-blue-400",
  };

  return (
    <div data-testid="admin-events">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 md:mb-8 gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white">Events</h1>
          <p className="text-neutral-400">Manage events and ticket types</p>
        </div>
        
        <Button className="btn-empire" onClick={() => handleOpenDialog()}>
          <Plus className="w-4 h-4 mr-2" />
          Create Event
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        <Card className="card-empire">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-white">{events.length}</div>
            <p className="text-sm text-neutral-400">Total Events</p>
          </CardContent>
        </Card>
        <Card className="card-empire">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-400">
              {events.filter(e => e.status === "published").length}
            </div>
            <p className="text-sm text-neutral-400">Published</p>
          </CardContent>
        </Card>
        <Card className="card-empire">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-yellow-400">
              {events.filter(e => e.status === "draft").length}
            </div>
            <p className="text-sm text-neutral-400">Drafts</p>
          </CardContent>
        </Card>
        <Card className="card-empire">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-empire-gold">
              {events.filter(e => e.is_featured).length}
            </div>
            <p className="text-sm text-neutral-400">Featured</p>
          </CardContent>
        </Card>
      </div>

      {/* Active/Inactive Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList className="bg-neutral-900 border border-neutral-800">
          <TabsTrigger value="published" className="data-[state=active]:bg-empire-gold data-[state=active]:text-black">
            Published ({events.filter(e => e.status === "published").length})
          </TabsTrigger>
          <TabsTrigger value="draft" className="data-[state=active]:bg-empire-gold data-[state=active]:text-black">
            Drafts ({events.filter(e => e.status === "draft").length})
          </TabsTrigger>
          <TabsTrigger value="cancelled" className="data-[state=active]:bg-empire-gold data-[state=active]:text-black">
            Cancelled ({events.filter(e => e.status === "cancelled").length})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Events Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-empire-gold animate-spin" />
        </div>
      ) : (() => {
        const filteredEvents = events.filter(e => e.status === activeTab);
        
        return filteredEvents.length === 0 ? (
          <Card className="card-empire">
            <CardContent className="py-12 text-center">
              <Calendar className="w-12 h-12 mx-auto mb-3 text-neutral-600" />
              <p className="text-neutral-500">No {activeTab} events found.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredEvents.map((event, index) => {
              const startDate = event.start_datetime ? parseISO(event.start_datetime) : new Date();
              
              return (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="card-empire overflow-hidden">
                    <div className="flex flex-col md:flex-row">
                      {/* Image */}
                      <div className="relative w-full md:w-48 h-40 md:h-auto flex-shrink-0">
                        <img
                          src={event.cover_image || "https://images.unsplash.com/photo-1508700929628-666bc8bd84ea?w=400"}
                          alt={event.name}
                          className="w-full h-full object-cover"
                        />
                      {event.is_featured && (
                        <Badge className="absolute top-2 left-2 bg-empire-gold text-black">
                          <Star className="w-3 h-3 mr-1 fill-current" />
                          Featured
                        </Badge>
                      )}
                    </div>

                    {/* Content */}
                    <CardContent className="flex-1 p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-bold text-white text-lg">{event.name}</h3>
                          <Badge className={`mt-1 ${statusColors[event.status]}`}>
                            {event.status}
                          </Badge>
                        </div>
                        <Badge variant="outline" className="border-neutral-700 text-neutral-400">
                          {event.event_type}
                        </Badge>
                      </div>

                      <p className="text-sm text-neutral-400 mb-3 line-clamp-2">
                        {event.description}
                      </p>

                      <div className="flex flex-wrap gap-3 text-sm text-neutral-500 mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4 text-empire-gold" />
                          {format(startDate, "MMM d, yyyy • h:mm a")}
                        </span>
                        {event.venue_name && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4 text-empire-gold" />
                            {event.venue_name}
                          </span>
                        )}
                      </div>

                      {/* Ticket Types */}
                      {event.ticket_types && event.ticket_types.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs text-neutral-500 mb-1">Ticket Types:</p>
                          <div className="flex flex-wrap gap-1">
                            {event.ticket_types.map((ticket) => (
                              <Badge key={ticket.id} variant="outline" className="text-xs border-neutral-700">
                                {ticket.name} - KES {ticket.price?.toLocaleString()}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex items-center justify-end gap-1 pt-3 border-t border-neutral-800">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(event)}
                          className="text-neutral-400 hover:text-white h-8 w-8"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(event.id, event.name)}
                          className="text-neutral-400 hover:text-red-400 h-8 w-8"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>
        );
      })()}

      {/* New Event Type Dialog */}
      <Dialog open={typeDialogOpen} onOpenChange={(open) => { if (!open) setNewTypeName(""); setTypeDialogOpen(open); }}>
        <DialogContent className="bg-neutral-900 border-neutral-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white">Create New Event Type</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateEventType} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white">Type Name *</Label>
              <Input
                value={newTypeName}
                onChange={(e) => setNewTypeName(e.target.value)}
                className="input-empire"
                placeholder="e.g., Battle, Recital, Audition"
                required
                autoFocus
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setTypeDialogOpen(false); setNewTypeName(""); }}
                className="flex-1 border-neutral-700"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={creatingType} className="flex-1 btn-empire">
                {creatingType ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Type"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Event Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setDialogOpen(open); }}>
        <DialogContent className="bg-neutral-900 border-neutral-800 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingEvent ? "Edit Event" : "Create New Event"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white">Event Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input-empire"
                placeholder="e.g., Summer Dance Showcase"
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input-empire min-h-[80px]"
                placeholder="Event description..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Event Type</Label>
                <Select value={formData.event_type} onValueChange={handleEventTypeChange}>
                  <SelectTrigger className="bg-neutral-900 border-neutral-800">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {eventTypes.map((et) => (
                      <SelectItem key={et.id} value={et.slug}>
                        {et.name}
                      </SelectItem>
                    ))}
                    <SelectItem value="__new__" className="text-empire-gold border-t border-neutral-800 mt-1 pt-1">
                      <span className="flex items-center gap-2">
                        <PlusCircle className="w-4 h-4" />
                        Add New Type
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-white">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger className="bg-neutral-900 border-neutral-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Start Date *</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="input-empire"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Start Time *</Label>
                <Input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  className="input-empire"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">End Date</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className="input-empire"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">End Time</Label>
                <Input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  className="input-empire"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Venue</Label>
              <Select value={formData.venue_type} onValueChange={handleVenueTypeChange}>
                <SelectTrigger className="bg-neutral-900 border-neutral-800">
                  <SelectValue placeholder="Select venue..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="edk_studio">Empire Dance Kenya Studio</SelectItem>
                  <SelectItem value="other">Other venue</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.venue_type === "edk_studio" && (
              <div className="flex items-start gap-2 bg-empire-gold/10 border border-empire-gold/30 rounded-xl px-3 py-2">
                <MapPin className="w-4 h-4 text-empire-gold mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-white">{EDK_STUDIO_NAME}</p>
                  <p className="text-xs text-neutral-400">{EDK_STUDIO_ADDRESS}</p>
                </div>
              </div>
            )}

            {formData.venue_type === "other" && (
              <>
                <div className="space-y-2">
                  <Label className="text-white">Venue Name</Label>
                  <Input
                    value={formData.venue_name}
                    onChange={(e) => setFormData({ ...formData, venue_name: e.target.value })}
                    className="input-empire"
                    placeholder="e.g., Nairobi National Theatre"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Venue Address</Label>
                  <Input
                    value={formData.venue_address}
                    onChange={(e) => setFormData({ ...formData, venue_address: e.target.value })}
                    className="input-empire"
                    placeholder="Full address"
                  />
                </div>
              </>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Max Capacity</Label>
                <Input
                  type="number"
                  value={formData.max_capacity}
                  onChange={(e) => setFormData({ ...formData, max_capacity: e.target.value })}
                  className="input-empire"
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <ImageUpload
                  label="Cover Image"
                  value={formData.cover_image}
                  onChange={(url) => setFormData({ ...formData, cover_image: url })}
                  folder="events"
                  placeholder="Upload or paste image URL"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_featured}
                onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })}
              />
              <Label className="text-white">Featured Event</Label>
            </div>

            {/* Highlights Section */}
            <div className="space-y-2">
              <Label className="text-white flex items-center gap-2">
                <Info className="w-4 h-4 text-empire-gold" />
                Event Highlights
              </Label>
              <p className="text-xs text-neutral-500">Add key info like &quot;Redeemable for food/drink&quot;, &quot;Group discounts&quot;, etc.</p>
              
              {/* Add Highlight Input */}
              <div className="flex gap-2">
                <Input
                  value={newHighlight}
                  onChange={(e) => setNewHighlight(e.target.value)}
                  className="input-empire flex-1"
                  placeholder="e.g., 1000/= Redeemable for food or drink"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (newHighlight.trim()) {
                        setFormData({ ...formData, highlights: [...formData.highlights, newHighlight.trim()] });
                        setNewHighlight("");
                      }
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  className="border-empire-gold text-empire-gold hover:bg-empire-gold hover:text-black"
                  onClick={() => {
                    if (newHighlight.trim()) {
                      setFormData({ ...formData, highlights: [...formData.highlights, newHighlight.trim()] });
                      setNewHighlight("");
                    }
                  }}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {/* Highlights List */}
              {formData.highlights.length > 0 && (
                <div className="space-y-2 mt-2">
                  {formData.highlights.map((highlight, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-neutral-800/50 px-3 py-2 rounded-lg">
                      <Star className="w-3 h-3 text-empire-gold flex-shrink-0" />
                      <span className="text-sm text-white flex-1">{highlight}</span>
                      <button
                        type="button"
                        onClick={() => setFormData({ 
                          ...formData, 
                          highlights: formData.highlights.filter((_, i) => i !== idx) 
                        })}
                        className="text-neutral-500 hover:text-red-400 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Ticket Types Section */}
            <div className="space-y-3 pt-2 border-t border-neutral-800">
              <Label className="text-white flex items-center gap-2">
                <Ticket className="w-4 h-4 text-empire-gold" />
                Ticket Types
              </Label>

              {/* Existing ticket types (edit mode) */}
              {editingEvent?.ticket_types?.filter(tt => !deletedTicketTypeIds.includes(tt.id)).map((tt) => (
                <div key={tt.id} className="flex items-center gap-2 bg-neutral-800/50 px-3 py-2 rounded-lg">
                  <Badge className={`text-[10px] px-1.5 ${TICKET_TYPE_COLORS[tt.ticket_type] || TICKET_TYPE_COLORS.regular}`}>
                    {TICKET_TYPE_LABELS[tt.ticket_type] || tt.ticket_type}
                  </Badge>
                  <span className="text-sm text-white flex-1">{tt.name}</span>
                  <span className="text-sm text-empire-gold font-medium">KES {Number(tt.price).toLocaleString()}</span>
                  <button
                    type="button"
                    onClick={() => removeExistingTicketType(tt.id)}
                    className="text-neutral-500 hover:text-red-400 transition-colors ml-1"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}

              {/* Pending new ticket types */}
              {pendingTicketTypes.map((tt, idx) => (
                <div key={idx} className="flex items-center gap-2 bg-empire-gold/5 border border-empire-gold/20 px-3 py-2 rounded-lg">
                  <Badge className={`text-[10px] px-1.5 ${TICKET_TYPE_COLORS[tt.ticket_type] || TICKET_TYPE_COLORS.regular}`}>
                    {TICKET_TYPE_LABELS[tt.ticket_type] || tt.ticket_type}
                  </Badge>
                  <span className="text-sm text-white flex-1">{tt.name}</span>
                  <span className="text-sm text-empire-gold font-medium">KES {Number(tt.price).toLocaleString()}</span>
                  <span className="text-[10px] text-neutral-500">new</span>
                  <button
                    type="button"
                    onClick={() => removePendingTicketType(idx)}
                    className="text-neutral-500 hover:text-red-400 transition-colors ml-1"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}

              {/* Add new ticket type inline */}
              <div className="bg-neutral-800/30 rounded-xl p-3 space-y-2 border border-neutral-800">
                <p className="text-xs text-neutral-500">Add ticket type</p>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    value={newTicketForm.name}
                    onChange={(e) => setNewTicketForm({ ...newTicketForm, name: e.target.value })}
                    className="input-empire h-9 text-sm"
                    placeholder="Name (e.g. Early Bird)"
                  />
                  <Select
                    value={newTicketForm.ticket_type}
                    onValueChange={(v) => setNewTicketForm({ ...newTicketForm, ticket_type: v })}
                  >
                    <SelectTrigger className="bg-neutral-900 border-neutral-800 h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="early_bird">Early Bird</SelectItem>
                      <SelectItem value="regular">Regular</SelectItem>
                      <SelectItem value="vip">VIP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    type="number"
                    value={newTicketForm.price}
                    onChange={(e) => setNewTicketForm({ ...newTicketForm, price: e.target.value })}
                    className="input-empire h-9 text-sm"
                    placeholder="Price (KES)"
                  />
                  <Input
                    type="number"
                    value={newTicketForm.quantity_available}
                    onChange={(e) => setNewTicketForm({ ...newTicketForm, quantity_available: e.target.value })}
                    className="input-empire h-9 text-sm"
                    placeholder="Qty available"
                  />
                  <Input
                    type="number"
                    value={newTicketForm.max_per_order}
                    onChange={(e) => setNewTicketForm({ ...newTicketForm, max_per_order: e.target.value })}
                    className="input-empire h-9 text-sm"
                    placeholder="Max/order"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addPendingTicketType}
                  disabled={!newTicketForm.name.trim() || !newTicketForm.price}
                  className="border-empire-gold/40 text-empire-gold hover:bg-empire-gold/10 text-xs"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add Ticket Type
                </Button>
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => { resetForm(); setDialogOpen(false); }}
                className="flex-1 border-neutral-700"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 btn-empire"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : editingEvent ? "Update Event" : "Create Event"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
